/**
 * Inscription et mot de passe (routes publiques, sans jeton : client axios brut).
 *
 * Extrait de l'ancien services/api.ts (Lot 8, déplacement pur).
 */

import axios from 'axios';
import type { ApiResponse } from '../types/api';
import { SignupRequestData, SignupResponse, CodeValidationResponse } from '../types/auth';
import { API_URL } from '../config/adminApi';
import { logger } from '../lib/logger';

/**
 * Message d'un échec : texte `error` du backend, sinon message de l'erreur,
 * sinon `fallback` (ordre historique de ces routes publiques).
 */
const failureMessage = (error: unknown, fallback: string): string => {
  const backendError = axios.isAxiosError(error)
    ? (error.response?.data as { error?: unknown } | undefined)?.error
    : undefined;
  if (backendError) {
    return String(backendError);
  }
  return (error instanceof Error && error.message) || fallback;
};

/** Routes publiques d'authentification (inscription, mot de passe, codes). */
export class AuthApiService {
  /**
   * Inscription d'un nouvel utilisateur avec code d'invitation
   */
  static async signup(data: SignupRequestData): Promise<SignupResponse> {
    try {
      const response = await axios.post<SignupResponse>(`${API_URL}/auth/signup`, data, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error: unknown) {
      logger.error('Signup error:', error);
      throw new Error(failureMessage(error, 'Erreur lors de l\'inscription'));
    }
  }

  /**
   * Envoyer un lien de réinitialisation de mot de passe à un admin
   */
  static async adminForgotPassword(email: string): Promise<ApiResponse<null>> {
    const response = await axios.post<ApiResponse<null>>(`${API_URL}/auth/admin/forgot-password`, { email }, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  }

  /**
   * Valider un code d'invitation
   */
  static async validateInvitationCode(code: string): Promise<CodeValidationResponse> {
    try {
      const response = await axios.post<CodeValidationResponse>(`${API_URL}/auth/validate-code`, {
        invitationCode: code
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error: unknown) {
      logger.error('Code validation error:', error);
      throw new Error(failureMessage(error, 'Code invalide'));
    }
  }
}

export default AuthApiService;
